// Purpose: /s/:token/files — recipient file list + zip download. Grant
// cookie assumed valid (issued by /verify). Shows banner if any file has a
// dangerous extension.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button, DataTable, type Column } from '../components/ui/index.ts';
import { AlertTriangleIcon, DownloadIcon, ClockIcon } from '../components/icons/index.tsx';
import { MonitoredNotice } from '../components/legal/MonitoredNotice.tsx';

interface FileEntry {
  id: string;
  originalFilename: string;
  sizeBytes: string;
  mimeType: string;
  dangerous: boolean;
  // Commit 8b.15: null = flat root file (pre-8b.15 default). SPA detects
  // any non-null entry to switch from the flat list to a tree view.
  relativePath: string | null;
}

interface ListResp {
  name: string | null;
  expiresAt: string | null;
  files: FileEntry[];
}

function fmt(bytes: string): string {
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function RecipientFiles() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ListResp | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    axios
      .get<ListResp>(`/api/public/share/${token}/files`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch((err) => {
        const status = (err as { response?: { status?: number } }).response?.status;
        // 410 (security audit 2026-10): the share was revoked, locked or
        // expired after the grant was minted; the landing page renders the
        // proper "no longer available" state from /meta.
        if (status === 401 || status === 410) {
          // SPA landing page — keep as a client-rendered route.
          window.location.href = `/s/${token}`;
          return;
        }
        setError('Unable to load files.');
      });
  }, [token]);

  if (error) {
    return (
      <div
        role="alert"
        className="bg-status-danger-muted text-status-danger border border-status-danger/30 rounded-md px-4 py-3 text-body-s"
      >
        {error}
      </div>
    );
  }
  if (!data) return <p className="text-body-m text-fg-secondary">Loading...</p>;

  const anyDangerous = data.files.some((f) => f.dangerous);
  // Commit 8b.15: switch from flat list to tree view if ANY file in the
  // share has a non-null relativePath. Pre-8b.15 shares are all-null and
  // render flat (no UX regression).
  const hasFolders = data.files.some((f) => f.relativePath && f.relativePath.length > 0);

  const columns: Column<FileEntry>[] = [
    {
      key: 'name',
      header: 'File',
      cell: (f) => (
        <span className="inline-flex items-center gap-2 text-body-m text-fg-primary truncate max-w-md">
          {f.dangerous ? (
            <AlertTriangleIcon size={14} className="text-status-warning" aria-label="Executable" />
          ) : null}
          <span className="truncate">{f.originalFilename}</span>
        </span>
      ),
    },
    {
      key: 'size',
      header: 'Size',
      align: 'right',
      hideOnMobile: true,
      cell: (f) => <span className="text-body-s text-fg-muted">{fmt(f.sizeBytes)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '160px',
      cell: (f) => (
        <a
          href={`/api/public/share/${token}/files/${f.id}/download`}
          className="inline-flex items-center gap-2 text-body-s text-accent-primary hover:underline"
        >
          <DownloadIcon size={14} />
          Download
        </a>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-3">
        <span className="text-label uppercase text-accent-primary">Secure File Share</span>
        <h1 className="font-display text-display-l text-fg-primary [font-feature-settings:var(--font-feature-display)]">
          {data.name ?? 'Share'}
        </h1>
        {data.expiresAt ? (
          <p className="inline-flex items-center gap-2 text-body-s text-fg-muted">
            <ClockIcon size={14} aria-hidden />
            <span>Available until {new Date(data.expiresAt).toLocaleString()}</span>
          </p>
        ) : null}
      </header>

      {anyDangerous ? (
        <div
          role="alert"
          className="bg-status-warning-muted text-status-warning border border-status-warning/30 rounded-md px-4 py-3 text-body-s flex items-start gap-3"
        >
          <AlertTriangleIcon size={16} aria-hidden className="flex-shrink-0 mt-0.5" />
          <span>
            This share contains files that may execute code on your device. Only proceed if you know
            the sender and are expecting these files.
          </span>
        </div>
      ) : null}

      <div>
        <Button
          variant="primary"
          size="lg"
          leftIcon={<DownloadIcon size={16} strokeWidth={2.5} />}
          onClick={() => {
            window.location.assign(`/api/public/share/${token}/download.zip`);
          }}
        >
          Download all as ZIP
        </Button>
        <MonitoredNotice className="mt-4" />
      </div>

      {hasFolders ? (
        <FolderTree token={token ?? ''} files={data.files} />
      ) : (
        <DataTable<FileEntry>
          columns={columns}
          rows={data.files}
          getRowKey={(f) => f.id}
          caption="Files in this share"
        />
      )}
    </div>
  );
}

// Commit 8b.15: tree-grouped file view for folder uploads. Files are
// grouped by their relativePath (null/empty = root group). Inside each
// group the same per-file download link as the flat list. The flat list
// renders identically when no file has a relativePath, preserving the
// pre-8b.15 recipient UX.
function FolderTree({ token, files }: { token: string; files: FileEntry[] }) {
  const groups = new Map<string, FileEntry[]>();
  for (const f of files) {
    const key = f.relativePath ?? '';
    const list = groups.get(key);
    if (list) list.push(f);
    else groups.set(key, [f]);
  }
  // Sort: root group first, then directories alphabetically.
  const sortedKeys = [...groups.keys()].sort((a, b) => {
    if (a === '') return -1;
    if (b === '') return 1;
    return a.localeCompare(b);
  });
  return (
    <div className="flex flex-col gap-4">
      {sortedKeys.map((key) => {
        const groupFiles = groups.get(key)!;
        return (
          <div key={key} className="flex flex-col gap-2">
            <div className="text-label uppercase text-fg-secondary">
              {key === '' ? 'Root' : key}
            </div>
            <ul className="divide-y divide-border-subtle">
              {groupFiles.map((f) => (
                <li key={f.id} className="py-2 flex items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-2 text-body-m text-fg-primary truncate min-w-0">
                    {f.dangerous ? (
                      <AlertTriangleIcon
                        size={14}
                        className="text-status-warning flex-shrink-0"
                        aria-label="Executable"
                      />
                    ) : null}
                    <span className="truncate">{f.originalFilename}</span>
                  </span>
                  <span className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-body-s text-fg-muted">{fmt(f.sizeBytes)}</span>
                    <a
                      href={`/api/public/share/${token}/files/${f.id}/download`}
                      className="inline-flex items-center gap-2 text-body-s text-accent-primary hover:underline"
                    >
                      <DownloadIcon size={14} />
                      Download
                    </a>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
